import { useMemo } from 'react';
import { useAppSelector } from '@/store';
import { IUserState, IRole, IOrganization, IProperty } from '@/types/admin/navigation';
import { UserType } from '@/types/residents/user';
import { selectSelectedIds } from '@/store/selectors/adminUISelectors';
import { IApiRole } from '@/types/admin/roles';

/**
 * Hook to transform auth user data into admin user format
 * This bridges the gap between the auth state and admin navigation requirements
 */
export const useAdminUser = (): IUserState => {
  const authUser = useAppSelector((state) => state.auth.user);
  const selectedIds = useAppSelector(selectSelectedIds);

  return useMemo(() => {
    if (!authUser) {
      return {
        id: 0,
        email: "",
        firstName: "",
        lastName: "",
        isSuperUser: false,
        organizations: [],
        properties: [],
        selectedOrganizationId: 0,
        selectedPropertyId: 0,
        roles: [],
        isLoading: false,
        emailCode: null,
        accessToken: null,
      };
    }

    // Determine if user is super user (from type or roles)
    const isSuperUser = authUser.type === UserType.SUPER_ADMIN || 
                        authUser.roles?.some((role: IApiRole) => role.name === 'Resio Admin') ||
                        false;

    // Transform roles to match IRole interface
    const transformedRoles: IRole[] = authUser.roles ? authUser.roles.map((role: IApiRole) => ({
      id: role.id,
      name: role.name,
      organizationId: role.organizationId,
      propertyId: role.propertyId || 0,
      organization: role.organization || { id: role.organizationId, name: 'Unknown Organization' },
      property: role.property || { id: role.propertyId || 0, marketingName: 'Unknown Property', organizationId: role.organizationId },
    })) : [];

    // Process organizations and properties from roles (matching admin-web logic exactly)
    const organizations = transformedRoles.map(role => role.organization);
    const properties = transformedRoles.flatMap(role => {
      if (role.property && role.property.id > 0) {
        return role.property;
      }
      if (role.organization?.properties) {
        return role.organization.properties;
      }
      // Return empty object like admin-web does (this gets filtered out later)
      return {} as IProperty;
    });

    // Remove duplicates (matching admin-web logic exactly)
    const propertyIds: number[] = [];
    const filteredProperties = properties.filter((value: IProperty) => {
      if (!propertyIds.includes(value.id)) {
        propertyIds.push(value.id);
        return value !== null && value.id > 0; // Filter out empty objects and invalid IDs
      }
      return false;
    });

    const orgIds: number[] = [];
    const filteredOrganizations = organizations.filter((value: IOrganization) => {
      // filter null and duplicate organizations
      if (!value || orgIds.includes(value.id)) {
        return false;
      }
      orgIds.push(value.id);
      // filter for organizations with at least one property
      return (
        (value.properties || []).length > 0 ||
        filteredProperties.find((property: IProperty) => property.organizationId === value.id)
      );
    });

    // Use selected IDs from UI state, or fall back to defaults (matching admin-web logic)
    const defaultOrgId = filteredOrganizations[0]?.id || 0;

    // Verify the stored org ID actually exists in the filtered list;
    // if the org was filtered out (e.g. no properties), fall back to the first valid org
    const selectedOrgExists = filteredOrganizations.some(
      (org) => org.id === selectedIds.organizationId
    );
    const selectedOrganizationId = selectedOrgExists
      ? selectedIds.organizationId
      : defaultOrgId;

    const defaultPropId = filteredProperties.find((property: IProperty) => {
      return property.organizationId === selectedOrganizationId;
    })?.id || filteredProperties[0]?.id || 0;

    // Verify the stored property ID actually exists in the filtered list
    const selectedPropExists = filteredProperties.some(
      (prop) => prop.id === selectedIds.propertyId
    );
    const selectedPropertyId = selectedPropExists
      ? selectedIds.propertyId
      : defaultPropId;

    return {
      id: authUser.id,
      email: authUser.email,
      firstName: authUser.firstName || "",
      lastName: authUser.lastName || "",
      isSuperUser,
      roles: transformedRoles,
      organizations: filteredOrganizations,
      properties: filteredProperties,
      selectedOrganizationId,
      selectedPropertyId,
      isLoading: false,
      emailCode: null,
      accessToken: null,
    };
  }, [authUser, selectedIds]);
};
